import React, { useState } from 'react';
import { Gamepad2, MonitorPlay, Zap, CheckCircle2, Clock, Package, ChevronDown, ChevronUp } from '../lib/icons';

const edgeJoystickSets = [
  {
    tier: 'Standard',
    name: 'K Silver TMR Joysticks',
    tagline: 'Drift-proof upgrade, drop-in replacement',
    color: '#00BFFF',
    features: [
      'K Silver TMR (Tunneling Magneto-Resistance) sensors',
      'Direct plug-in replacement for DualSense Edge modules',
      'Eliminates stick drift permanently',
      '90-day warranty on installation',
    ],
    capOptions: null,
  },
  {
    tier: 'Performance',
    name: 'GuliKit TMR Joysticks',
    tagline: 'Premium sensors, zero drift',
    color: '#39FF14',
    features: [
      'GuliKit TMR magnetic sensors',
      'Industry-leading precision and consistency',
      'Direct plug-in replacement for DualSense Edge modules',
      'No calibration needed after install',
      '90-day warranty on installation',
    ],
    capOptions: null,
  },
  {
    tier: 'Performance+',
    name: 'GuliKit TMR — Adjustable Tension',
    tagline: 'Full control over feel and reach',
    color: '#FF1744',
    features: [
      'GuliKit TMR magnetic sensors',
      'On-the-fly tension adjustment (no tools needed)',
      'Choose your stick cap height at order time',
      '90-day warranty on installation',
    ],
    capOptions: ['Standard (Short)', 'Tall', 'Taller'],
  },
];

const ConsolesShop = () => {
  const [capsOpen, setCapsOpen] = useState(false);
  const consoles = [
    {
      name: 'Nintendo Switch (LCD)',
      type: 'nintendo',
      icon: Gamepad2,
      description: 'Classic Nintendo Switch with 6.2" LCD screen',
      features: [
        'Play at home or on-the-go',
        '6.2" LCD multi-touch screen',
        'Joy-Con controllers included',
        'Dock for TV play'
      ],
      availability: 'In Stock',
      color: 'from-[#FF1744] to-[#C62828]',
      borderColor: 'border-[#FF1744]/50'
    },
    {
      name: 'Nintendo Switch (OLED)',
      type: 'nintendo',
      icon: MonitorPlay,
      description: 'Enhanced Switch with vibrant 7" OLED display',
      features: [
        'Vivid 7" OLED screen',
        'Enhanced audio',
        'Wider adjustable stand',
        'Dock with wired LAN port'
      ],
      availability: 'In Stock',
      color: 'from-[#FF1744] to-[#D32F2F]',
      borderColor: 'border-[#FF1744]/50'
    },
    {
      name: 'Nintendo Switch 2',
      type: 'nintendo',
      icon: Zap,
      description: 'Next-gen Nintendo console',
      features: [
        'Next-generation hardware',
        'Enhanced performance',
        'Backward compatible',
        'More details coming soon'
      ],
      availability: 'Coming Soon',
      color: 'from-[#00BFFF] to-[#0288D1]',
      borderColor: 'border-[#00BFFF]/50',
      comingSoon: true
    },
    {
      name: 'PlayStation 5',
      type: 'playstation',
      icon: Package,
      description: 'Sony\'s flagship gaming console',
      features: [
        'Ultra-high speed SSD',
        'Ray tracing support',
        '4K gaming at 120Hz',
        'DualSense controller included'
      ],
      availability: 'In Stock',
      color: 'from-[#00BFFF] to-[#0277BD]',
      borderColor: 'border-[#00BFFF]/50'
    },
    {
      name: 'Xbox Series X',
      type: 'xbox',
      icon: Package,
      description: 'Microsoft\'s most powerful console',
      features: [
        '12 teraflops of power',
        'True 4K gaming',
        'Quick Resume feature',
        'Game Pass compatible'
      ],
      availability: 'In Stock',
      color: 'from-[#39FF14] to-[#2ECC40]',
      borderColor: 'border-[#39FF14]/50'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-black to-[#0D0D0D] text-[#E0E0E0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-6 py-2 mb-6">
            <Package className="h-5 w-5 text-[#39FF14]" />
            <span className="text-[#39FF14] font-semibold">CONSOLES FOR SALE</span>
          </div>
          <h2 className="text-5xl font-bold mb-4">Gaming Consoles</h2>
          <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
            Brand new consoles ready for pickup. Contact us for current pricing and availability. All systems come with manufacturer warranty.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {consoles.map((console, index) => (
            <div
              key={index}
              className={`bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border ${console.borderColor} hover:shadow-lg hover:shadow-${console.color.split('-')[1]}/20 transition-all duration-300 ${console.comingSoon ? 'relative overflow-hidden' : ''}`}
            >
              {console.comingSoon && (
                <div className="absolute top-4 right-4 bg-[#00BFFF] text-black px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>COMING SOON</span>
                </div>
              )}

              <div className={`bg-gradient-to-br ${console.color} p-3 rounded-lg w-fit mb-4`}>
                <console.icon className="h-8 w-8 text-white" />
              </div>

              <h3 className="text-2xl font-bold mb-2">{console.name}</h3>
              <p className="text-[#B0B0B0] mb-4">{console.description}</p>

              <div className="space-y-2 mb-6">
                {console.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <CheckCircle2 className={`h-5 w-5 ${console.comingSoon ? 'text-[#00BFFF]' : 'text-[#39FF14]'} flex-shrink-0 mt-0.5`} />
                    <span className="text-sm text-[#E0E0E0]">{feature}</span>
                  </div>
                ))}
              </div>

            </div>
          ))}
        </div>

        {/* PS5 DualSense Edge — Joystick Replacement Sets */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center space-x-2 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-full px-6 py-2 mb-6">
              <Gamepad2 className="h-5 w-5 text-[#FF1744]" />
              <span className="text-[#FF1744] font-semibold">PS5 DUALSENSE EDGE</span>
            </div>
            <h2 className="text-4xl font-bold text-[#E0E0E0] mb-3">Joystick Replacement Sets</h2>
            <p className="text-[#B0B0B0] text-lg max-w-2xl mx-auto">
              The DualSense Edge uses swappable stick modules — we offer three upgrade tiers to permanently eliminate drift.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {edgeJoystickSets.map((set) => (
              <div
                key={set.tier}
                className="bg-gray-900/50 rounded-xl p-6 border transition-all duration-300"
                style={{ borderColor: `${set.color}40` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                    style={{ backgroundColor: `${set.color}20`, color: set.color }}
                  >
                    {set.tier}
                  </span>
                  <span className="text-xs text-[#B0B0B0] italic font-medium bg-gray-800 px-3 py-1 rounded-full">
                    Pricing coming soon
                  </span>
                </div>

                <h3 className="text-xl font-bold text-[#E0E0E0] mb-1">{set.name}</h3>
                <p className="text-sm mb-5" style={{ color: set.color }}>{set.tagline}</p>

                <ul className="space-y-2.5 mb-5">
                  {set.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#B0B0B0]">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: set.color }} />
                      {f}
                    </li>
                  ))}
                </ul>

                {set.capOptions && (
                  <div className="border-t border-gray-800 pt-4">
                    <button
                      onClick={() => setCapsOpen(o => !o)}
                      className="flex items-center justify-between w-full text-sm font-semibold text-[#E0E0E0] mb-2"
                    >
                      <span>Stick cap height options</span>
                      {capsOpen ? <ChevronUp className="h-4 w-4 text-[#B0B0B0]" /> : <ChevronDown className="h-4 w-4 text-[#B0B0B0]" />}
                    </button>
                    {capsOpen && (
                      <div className="flex gap-2 flex-wrap">
                        {set.capOptions.map(cap => (
                          <span
                            key={cap}
                            className="text-xs px-3 py-1.5 rounded-lg border font-semibold"
                            style={{ borderColor: `${set.color}50`, color: set.color, backgroundColor: `${set.color}10` }}
                          >
                            {cap}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-[#B0B0B0] text-sm mb-4">Ready to upgrade your DualSense Edge sticks?</p>
            <a
              href={`mailto:info@gamefixsolutions.com?subject=${encodeURIComponent('DualSense Edge Joystick Replacement')}&body=${encodeURIComponent('Hi, I\'m interested in a joystick replacement for my DualSense Edge.\n\nPreferred option:\n\nAny questions:\n')}`}
              className="inline-flex items-center gap-2 bg-[#FF1744] hover:bg-[#39FF14] text-white hover:text-black px-8 py-3 rounded-lg font-semibold transition-all duration-200"
            >
              <Gamepad2 className="h-5 w-5" />
              Get a Quote
            </a>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-gray-900 to-black rounded-2xl p-8 border border-gray-800">
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-4">Ready to Get Your Console?</h3>
            <p className="text-[#B0B0B0] text-lg mb-6 max-w-2xl mx-auto">
              Contact us for current pricing and availability. We can also help with setup, accessories, and game recommendations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#contact"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF1744] to-[#00BFFF] hover:from-[#00BFFF] hover:to-[#39FF14] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <Package className="h-5 w-5" />
                <span>Contact Us for Pricing</span>
              </a>
            </div>
            <p className="text-[#B0B0B0] text-sm mt-4">
              Walk-ins welcome or schedule an appointment
            </p>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 text-center">
            <CheckCircle2 className="h-10 w-10 text-[#39FF14] mx-auto mb-3" />
            <h4 className="text-lg font-bold mb-2">Authentic Products</h4>
            <p className="text-[#B0B0B0] text-sm">100% genuine consoles with full manufacturer warranty</p>
          </div>
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 text-center">
            <Package className="h-10 w-10 text-[#00BFFF] mx-auto mb-3" />
            <h4 className="text-lg font-bold mb-2">Expert Setup</h4>
            <p className="text-[#B0B0B0] text-sm">We can help you get set up and ready to play</p>
          </div>
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 text-center">
            <Zap className="h-10 w-10 text-[#FF1744] mx-auto mb-3" />
            <h4 className="text-lg font-bold mb-2">Trade-Ins Welcome</h4>
            <p className="text-[#B0B0B0] text-sm">Ask about trading in your old console</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsolesShop;
